import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ILegalTransaction,
  ILegalTransactionFullObject,
} from '../../Interfaces/ILegalTransaction';
import { ILegalTransactionMeeting } from '../../Interfaces/ILegalTransactionMeeting';
import { ILegalTransactionProgress } from '../../Interfaces/ILegalTransactionProgress';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionSpecialFunctions } from '../../Interfaces/ILegalTransactionSpecialFunctions';
import { ILegalTransactionState } from '../../Interfaces/ILegalTransactionState';
import { ILegalTransactionTransactionNumber } from '../../Interfaces/ILegalTransactionTransactionNumber';
import {
  ILegalTransactionType,
  ILegalTransactionTypeFullObject,
} from '../../Interfaces/ILegalTransactionType';
import { customFetcher } from '../useFetch';

/**
 * Felder des Vorgangs, die im Modul als `boolean` deklariert sind. Sie stehen
 * in der Datenbank als TINYINT und kommen deshalb beim Lesen als `0`/`1`
 * zurueck.
 */
const BOOLEAN_FIELD_ARRAY = [
  'UserInformed_LackOfActivity',
  'syncWithArnoTop',
  'RequestClientMeeting',
  'RequestClientData',
] as const;

/**
 * Boolean-Felder als echte Wahrheitswerte senden.
 *
 * Der Swagger-Validator des FullObject-Save akzeptiert nur `boolean`/`null`;
 * ein rohes `0` quittiert er mit HTTP 400 („0 is not of type 'boolean',
 * 'null'"). Wer ein geladenes fullObject unveraendert zurueckschickt, laeuft
 * genau da hinein — die Vorgangsmaske hat die Felder einzeln abgefangen, das
 * Einsortieren aus dem Posteingang nicht, und in Produktion ist es dort
 * aufgeschlagen.
 *
 * Deshalb hier und nicht beim Aufrufer: so kann es kein bestehender und kein
 * kuenftiger Aufrufer vergessen. `null` bleibt `null` — das Feld ist nullable,
 * und `Boolean(null)` waere `false` und damit eine andere Aussage.
 */
const withBooleanFlags = (
  uploadObject: ILegalTransactionFullObject
): ILegalTransactionFullObject => {
  const normalized = { ...uploadObject };

  BOOLEAN_FIELD_ARRAY.forEach(field => {
    const value = normalized[field];

    if (value !== null && value !== undefined) {
      // Der Zugriff ueber den Feldnamen ist typseitig nicht eng genug fuer
      // eine Zuweisung; die Namen stehen aber fest in BOOLEAN_FIELD_ARRAY.
      (normalized as Record<string, unknown>)[field] = Boolean(value);
    }
  });

  return normalized;
};

//////////////////// LegalTransactions
export const useSearchLegalTransactions = (searchUri?: string) => {
  const abortController = new AbortController();

  const {
    data: legalTransactions,
    isLoading: isLoadingLegalTransactions,
    refetch: refetchLegalTransactions,
  } = useQuery<ILegalTransaction[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransaction', 'search', searchUri],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction?${searchUri}`,
        signal: abortController.signal,
      }),
    enabled: !!searchUri,
  });

  const abort = () => {
    if (isLoadingLegalTransactions) {
      abortController.abort();
    }
  };

  return {
    legalTransactions,
    isLoadingLegalTransactions,
    refetchLegalTransactions,
    abortSearchLegalTransactions: abort,
  };
};

export const useGetLegalTransactions = (enableQuery: boolean = true) => {
  const {
    data: legalTransactions,
    isLoading: isLoadingLegalTransactions,
    refetch: refetchLegalTransactions,
  } = useQuery<ILegalTransaction[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransaction'],
    queryFn: () => customFetcher({ endpoint: '/legaltransaction' }),
    enabled: enableQuery,
  });

  return {
    legalTransactions,
    isLoadingLegalTransactions,
    refetchLegalTransactions,
  };
};

export const useDeleteLegalTransaction = () => {
  const {
    mutateAsync: deleteLegalTransaction,
    isPending: isPendingDeleteLegalTransaction,
  } = useMutation({
    mutationFn: (idLegalTransaction: number) =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}`,
        method: 'DELETE',
      }),
  });

  return { deleteLegalTransaction, isPendingDeleteLegalTransaction };
};

//////////////////// LegalTransactionFullObject
export const useGetLegalTransactionFullObject = (
  idLegalTransaction?: number
) => {
  const {
    data: legalTransactionFullObject,
    isFetching: isFetchingLegalTransactionFullObject,
    refetch: refetchLegalTransactionFullObject,
  } = useQuery<ILegalTransactionFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransaction/fullObject', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction/fullObject/${idLegalTransaction}`,
      }),
    enabled: !!idLegalTransaction,
  });

  return {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  };
};

export const useCreateLegalTransactionFullObject = () => {
  const {
    mutateAsync: createLegalTransactionFullObject,
    isPending: isPendingCreateLegalTransactionFullObject,
  } = useMutation({
    mutationFn: (uploadObject: ILegalTransactionFullObject) =>
      customFetcher({
        endpoint: '/legaltransaction/fullObject',
        body: withBooleanFlags(uploadObject),
        method: 'POST',
      }),
  });

  return {
    createLegalTransactionFullObject,
    isPendingCreateLegalTransactionFullObject,
  };
};

export const useUpdateLegalTransactionFullObject = () => {
  const {
    mutateAsync: updateLegalTransactionFullObject,
    isPending: isPendingUpdateLegalTransactionFullObject,
  } = useMutation({
    mutationFn: (uploadObject: ILegalTransactionFullObject) =>
      customFetcher({
        endpoint: '/legaltransaction/fullObject',
        body: withBooleanFlags(uploadObject),
        method: 'PUT',
      }),
  });

  return {
    updateLegalTransactionFullObject,
    isPendingUpdateLegalTransactionFullObject,
  };
};

export const useReactiveLegalTransactionFullObject = () => {
  const {
    mutateAsync: reactiveLegalTransactionFullObject,
    isPending: isPendingReactiveLegalTransactionFullObject,
  } = useMutation({
    mutationFn: (uploadObject: ILegalTransactionFullObject) =>
      customFetcher({
        endpoint: '/legaltransaction/reactive',
        body: uploadObject,
        method: 'PUT',
      }),
  });

  return {
    reactiveLegalTransactionFullObject,
    isPendingReactiveLegalTransactionFullObject,
  };
};

//////////////////// LegalTransactionTypes
export const useGetLegalTransactionTypes = () => {
  const {
    data: legalTransactionTypes,
    isLoading: isLoadingLegalTransactionTypes,
    refetch: refetchLegalTransactionTypes,
  } = useQuery<ILegalTransactionType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactiontype'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactiontype' }),
  });

  return {
    legalTransactionTypes,
    isLoadingLegalTransactionTypes,
    refetchLegalTransactionTypes,
  };
};

//////////////////// LegalTransactionTypeFullObjects
export const useGetLegalTransactionTypeFullObject = (
  idLegalTransactionType: number
) => {
  const {
    data: legalTransactionTypeFullObject,
    isLoading: isLoadingLegalTransactionTypeFullObject,
    isPending: isLegalTransactionTypeFullObjectPending,
    refetch: refetchLegalTransactionTypeFullObject,
  } = useQuery<ILegalTransactionTypeFullObject>({
    queryKey: ['legaltransactiontype/fullObject', idLegalTransactionType],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransactiontype/fullObject/${idLegalTransactionType}`,
      }),
    // Makes sure the query is not executed when the id is 0
    enabled: !!idLegalTransactionType,
  });

  return {
    isLegalTransactionTypeFullObjectPending,
    legalTransactionTypeFullObject,
    isLoadingLegalTransactionTypeFullObject,
    refetchLegalTransactionTypeFullObject,
  };
};

//////////////////// LegalTransactionSpecialFunctions
export const useGetLegalTransactionSpecialFunctions = () => {
  const {
    data: legalTransactionSpecialFunctions,
    isLoading: isLoadingLegalTransactionSpecialFunctions,
  } = useQuery<ILegalTransactionSpecialFunctions[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactionspecialfunctions'],
    queryFn: () =>
      customFetcher({ endpoint: '/legaltransactionspecialfunctions' }),
  });

  return {
    legalTransactionSpecialFunctions,
    isLoadingLegalTransactionSpecialFunctions,
  };
};

//////////////////// LegalTransactionProgresses
export const useGetLegalTransactionProgresses = () => {
  const {
    data: legalTransactionProgresses,
    isLoading: isLoadingLegalTransactionProgresses,
    refetch: refetchLegalTransactionProgresses,
  } = useQuery<ILegalTransactionProgress[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactionprogress'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionprogress' }),
  });

  return {
    legalTransactionProgresses,
    isLoadingLegalTransactionProgresses,
    refetchLegalTransactionProgresses,
  };
};

//////////////////// LegalTransactionStates
export const useGetLegalTransactionStates = () => {
  const {
    data: legalTransactionStates,
    isLoading: isLoadingLegalTransactionStates,
    refetch: refetchLegalTransactionStates,
  } = useQuery<ILegalTransactionState[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactionstate'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionstate' }),
  });

  return {
    legalTransactionStates,
    isLoadingLegalTransactionStates,
    refetchLegalTransactionStates,
  };
};

//////////////////// LegalTransactionRecordNumbers
export const useGetLegalTransactionRecordNumbers = () => {
  const {
    data: legalTransactionRecordNumbers,
    isLoading: isLoadingLegalTransactionRecordNumbers,
  } = useQuery<ILegalTransactionRecordNumber[]>({
    queryKey: ['legaltransactionrecordnumber'],
    queryFn: () =>
      customFetcher({ endpoint: '/legaltransaction/recordnumber' }),
  });

  return {
    legalTransactionRecordNumbers,
    isLoadingLegalTransactionRecordNumbers,
  };
};

//////////////////// LegalTransactionTransactionNumbers
export const useGetLegalTransactionTransactionNumbers = () => {
  const {
    data: legalTransactionTransactionNumbers,
    isLoading: isLoadingLegalTransactionTransactionNumbers,
  } = useQuery<ILegalTransactionTransactionNumber[]>({
    queryKey: ['legaltransactiontransactionnumber'],
    queryFn: () =>
      customFetcher({ endpoint: '/legaltransaction/transactionnumber' }),
  });

  return {
    legalTransactionTransactionNumbers,
    isLoadingLegalTransactionTransactionNumbers,
  };
};

//////////////////// LegalTransactionMeetings
export const useGetLegalTransactionMeetings = () => {
  const {
    data: legalTransactionMeetings,
    isLoading: isLoadingLegalTransactionMeetings,
  } = useQuery<ILegalTransactionMeeting[]>({
    queryKey: ['legaltransactionmeeting'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionmeeting' }),
  });

  return { legalTransactionMeetings, isLoadingLegalTransactionMeetings };
};
